interface Column<T> {
  header: string;
  accessor: keyof T | ((row: T) => React.ReactNode);
  className?: string;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  onRowClick?: (row: T) => void;
  isLoading?: boolean;
  emptyMessage?: string;
}

export default function DataTable<T extends { id: string }>({
  data,
  columns,
  onRowClick,
  isLoading = false,
  emptyMessage = 'No data available',
}: DataTableProps<T>) {
  if (isLoading) {
    return (
      <div className="overflow-x-auto animate-pulse">
        <table className="min-w-full divide-y divide-glass">
          <thead className="bg-glass-1">
            <tr>
              {columns.map((_, index) => (
                <th
                  key={index}
                  className="px-6 py-3 text-left text-[0.7rem] font-medium uppercase tracking-[0.05em] text-glass-text-2"
                >
                  <div className="h-3 w-16 max-w-full rounded bg-glass-3" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-glass bg-transparent">
            {Array.from({ length: 8 }).map((_, ri) => (
              <tr key={ri}>
                {columns.map((_, ci) => (
                  <td key={ci} className="whitespace-nowrap px-6 py-4">
                    <div
                      className="h-4 rounded bg-glass-3"
                      style={{ width: `${45 + ((ri * 3 + ci * 11) % 45)}%` }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-glass-text-2">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-glass">
        <thead className="bg-glass-1">
          <tr>
            {columns.map((column, index) => (
              <th
                key={index}
                className="px-6 py-3 text-left text-[0.7rem] font-medium text-glass-text-2 uppercase tracking-[0.05em]"
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-transparent divide-y divide-glass">
          {data.map((row) => (
            <tr
              key={row.id}
              onClick={() => onRowClick?.(row)}
              className={onRowClick ? 'cursor-pointer hover:bg-glass-3 transition-colors' : ''}
            >
              {columns.map((column, index) => (
                <td
                  key={index}
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    column.className || 'text-glass-text'
                  }`}
                >
                  {typeof column.accessor === 'function'
                    ? column.accessor(row)
                    : String(row[column.accessor] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
